import { type Emissary, emissaryWebsocketApi, MessageType } from 'api-validations/emissary'
import { DataSource } from 'api-validations/shared'
import type React from 'react'
import { useEffect, useMemo } from 'react'
import { create } from 'zustand'
import { devtools, subscribeWithSelector } from 'zustand/middleware'

import { ManagedWebsocket } from '../../lib/websocket'
import { useOrgCodeOrThrow } from '../active-operation'
import { useApiClient } from '../api-provider'
import {
  type AccountDevice,
  EMPTY_ROSTER,
  isHeard,
  type LiveRoster,
  liveDevices,
  nextSettleAt,
  receiveAccount,
  settleRoster
} from './liveRoster'
import type { UnlabeledStream } from './useAllStreams'

type SerialNumber = number
type StreamId = string
type DeviceLocation = NonNullable<Emissary.Data['location']>
type DeviceSpeed = NonNullable<Emissary.Data['speed']>
type SoftwareVersion = NonNullable<Emissary.About['version']>

export type EmissaryStore = {
  /**
   * Map of serial numbers to live device locations.
   */
  locations: Map<SerialNumber, DeviceLocation>
  /**
   * All stream ids that are currently live.
   */
  liveStreamIds: Set<StreamId>
  /**
   * Map of serial numbers to their stream id.
   */
  liveStreamIdLookup: Map<SerialNumber, StreamId>
  setDeviceData: (messages: Emissary.Data[]) => void
  /**
   * Set of serial numbers that have been added to the store.
   */
  activeDbms: Set<SerialNumber>
  /**
   * Map of stream ids to their metadata, like client, farm, field,
   * task type, etc.
   */
  liveAccountConfig: Map<StreamId, UnlabeledStream>
  setLiveStreams: (liveStreams: AccountDevice[]) => void
  /**
   * Map of serial numbers to the software version of the device.
   */
  deviceSoftwareVersion: Map<SerialNumber, SoftwareVersion>
  /**
   * Map of serial numbers to the speed of the device.
   */
  deviceSpeed: Map<SerialNumber, DeviceSpeed>
  setSoftwareVersion: (serialNumber: SerialNumber, version: SoftwareVersion) => void
  /**
   * Clear all data from the store.
   */
  clear: () => void
}

const toLiveStream = (device: AccountDevice): UnlabeledStream => ({
  streamId: device.streamId,
  crop: device.crop,
  customCrop: undefined,
  cropKey: device.crop,
  serialNumber: device.serialNumber,
  season: device.season,
  task: device.task.key,
  taskLabel: device.task.name,
  passName: undefined,
  client: device.client,
  farm: device.farm,
  field: device.field,
  uuid: undefined,
  taskIndex: device.taskIndex,
  lastModified: new Date(),
  isLive: true,
  // Only Gen3 displays can connect to Panorama live. Revisit if we get a live API from another
  // provider.
  dataSource: DataSource.Gen3
})

/**
 * Store for managing emissary data.
 */
export const useLiveStreamData = create<EmissaryStore>()(
  devtools(
    subscribeWithSelector((set) => ({
      activeDbms: new Set(),
      locations: new Map(),
      liveStreamIdLookup: new Map(),
      setDeviceData(messages) {
        set((prev) => {
          const locations = new Map(prev.locations)
          const deviceSpeed = new Map(prev.deviceSpeed)
          for (const { serialNumber, location, speed } of messages) {
            if (location !== undefined) locations.set(serialNumber, location)
            if (speed !== undefined) {
              deviceSpeed.set(serialNumber, speed.value < 0.05 ? { ...speed, value: 0 } : speed)
            }
          }
          return { locations, deviceSpeed }
        })
      },
      liveAccountConfig: new Map(),
      liveStreamIds: new Set(),
      deviceSpeed: new Map(),
      deviceSoftwareVersion: new Map(),
      setSoftwareVersion(serialNumber, version) {
        set((prev) => ({
          deviceSoftwareVersion: new Map(prev.deviceSoftwareVersion).set(serialNumber, version)
        }))
      },
      setLiveStreams(liveStreams) {
        set({
          activeDbms: liveStreams.reduce(
            (set, { serialNumber }) => set.add(serialNumber),
            new Set<number>()
          ),
          liveAccountConfig: new Map(
            liveStreams.map((stream) => [stream.streamId, toLiveStream(stream)])
          ),
          liveStreamIds: new Set(liveStreams.map((d) => d.streamId)),
          liveStreamIdLookup: new Map(liveStreams.map((d) => [d.serialNumber, d.streamId]))
        })
      },
      clear() {
        set({
          activeDbms: new Set(),
          locations: new Map(),
          liveStreamIdLookup: new Map(),
          liveAccountConfig: new Map(),
          liveStreamIds: new Set(),
          deviceSpeed: new Map(),
          deviceSoftwareVersion: new Map()
        })
      }
    })),
    { name: 'EmissaryLiveData' }
  )
)

const HEARTBEAT_TIMEOUT = 30 * 1000 // 30 seconds (the heartbeat is actually expected every 5 seconds)

let roster: LiveRoster = EMPTY_ROSTER
let settleTimer: ReturnType<typeof setTimeout> | undefined

const publishRoster = (next: LiveRoster) => {
  roster = next
  useLiveStreamData.getState().setLiveStreams(liveDevices(roster))

  clearTimeout(settleTimer)
  const settleAt = nextSettleAt(roster)
  if (settleAt === undefined) return
  settleTimer = setTimeout(
    () => publishRoster(settleRoster({ roster, now: Date.now() })),
    settleAt - Date.now()
  )
}

// Every store update can interrupt a page render in progress, so messages that arrive at 5 Hz
// per device are applied together.
const DEVICE_DATA_FLUSH_MS = 200

let pendingDeviceData: Emissary.Data[] = []
let deviceDataTimer: ReturnType<typeof setTimeout> | undefined

const flushDeviceData = () => {
  deviceDataTimer = undefined
  useLiveStreamData.getState().setDeviceData(pendingDeviceData)
  pendingDeviceData = []
}

const queueDeviceData = (message: Emissary.Data) => {
  pendingDeviceData.push(message)
  deviceDataTimer ??= setTimeout(flushDeviceData, DEVICE_DATA_FLUSH_MS)
}

const resetLiveData = () => {
  clearTimeout(settleTimer)
  clearTimeout(deviceDataTimer)
  deviceDataTimer = undefined
  roster = EMPTY_ROSTER
  pendingDeviceData = []
  useLiveStreamData.getState().clear()
}

const FIRST_ROSTER_WAIT_MS = 5_000

export const liveStreamsOnceHeard = (): Promise<ReadonlyMap<StreamId, UnlabeledStream>> => {
  const current = () => useLiveStreamData.getState().liveAccountConfig
  if (isHeard(roster)) return Promise.resolve(current())

  return new Promise((resolve) => {
    const settle = () => {
      unsubscribe()
      clearTimeout(timer)
      resolve(current())
    }
    const unsubscribe = useLiveStreamData.subscribe(
      (state) => state.liveAccountConfig,
      () => {
        if (isHeard(roster)) settle()
      }
    )
    const timer = setTimeout(settle, FIRST_ROSTER_WAIT_MS)
  })
}

const handleMessage = (data: Emissary.Message['message'][number]) => {
  switch (data.type) {
    case MessageType.Account:
      publishRoster(receiveAccount({ roster, devices: data.devices, now: Date.now() }))
      break

    case MessageType.Data:
      queueDeviceData(data)
      break

    case MessageType.About:
      useLiveStreamData.getState().setSoftwareVersion(data.serialNumber, data.version)
      break

    case MessageType.Unknown:
      console.error('Unknown message type:', data)
      break

    case MessageType.Heartbeat:
      break
    default:
      break
  }
}

export const EmissaryWebsocketProvider = ({
  domain,
  children
}: {
  domain: string
  children: React.ReactNode
}) => {
  const orgCode = useOrgCodeOrThrow()
  const apiClient = useApiClient()

  const client = useMemo(
    () =>
      new ManagedWebsocket({
        name: 'Emissary',
        path: ({ access_token }) =>
          emissaryWebsocketApi.pathFor({ orgCode, access_token: access_token, domain }),
        getAccessToken: () => apiClient.getAccessToken(),
        onMessage: (event) => {
          const data = emissaryWebsocketApi.responseSchema.parse(event.data)
          data.message.forEach(handleMessage)
        },
        heartbeatTimeoutMs: HEARTBEAT_TIMEOUT,
        allowInBackground: false,
        onCleanup: resetLiveData
      }),
    [orgCode, apiClient, domain]
  )

  useEffect(() => {
    client.connect()
    return () => client.disconnect()
  }, [client])

  return children
}
